import type { AllowlistSegment, ExchangeMaintenanceAllowlist } from '@/services/api/exchange-maintenance';

/** The API caps the list at what an SSM parameter holds. */
export const MAX_ALLOWLIST_ACCOUNT_HOLDER_IDS = 100;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface ParsedAccountHolderIds {
  /** Lower-cased, in the order first seen, without duplicates. */
  ids: string[];
  /** Entries that are not UUIDs, as typed. */
  invalid: string[];
}

/** Account holder ids pasted one per line, or separated by commas or spaces. */
export const parseAccountHolderIds = (text: string): ParsedAccountHolderIds => {
  const entries = text
    .split(/[\s,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  const ids = [...new Set(entries.map((entry) => entry.toLowerCase()).filter((entry) => UUID.test(entry)))];
  const invalid = entries.filter((entry) => !UUID.test(entry.toLowerCase()));
  return { ids, invalid };
};

export const formatAccountHolderIds = (ids: readonly string[]): string => ids.join('\n');

/** Whether two lists let the same callers through, whatever their order. */
export const isSameAllowlist = (a: ExchangeMaintenanceAllowlist, b: ExchangeMaintenanceAllowlist): boolean =>
  [...a.segments].sort().join() === [...b.segments].sort().join() && [...a.account_holder_ids].sort().join() === [...b.account_holder_ids].sort().join();

export const isEmptyAllowlist = (allowlist: ExchangeMaintenanceAllowlist): boolean => allowlist.segments.length === 0 && allowlist.account_holder_ids.length === 0;

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** One line for the page and the confirm dialog, in the page's words: "Brokers and 2 staff accounts". */
export const describeAllowlist = (allowlist: ExchangeMaintenanceAllowlist): string => {
  if (isEmptyAllowlist(allowlist)) return 'nobody';
  const count = allowlist.account_holder_ids.length;
  const parts = [...allowlist.segments.map((segment) => SEGMENT_COPY[segment].label), ...(count ? [plural(count, 'staff account')] : [])];
  return parts.join(', ').replace(/, ([^,]*)$/, ' and $1');
};

export const SEGMENT_COPY: Record<AllowlistSegment, { label: string; description: string; isEnforced: boolean }> = {
  BROKER: { label: 'Brokers', description: 'Every broker API request', isEnforced: true },
  MARKET_MAKER: { label: 'Market makers', description: 'Market maker accounts', isEnforced: false },
  INSTITUTIONAL: { label: 'Institutional', description: 'Institutional accounts', isEnforced: false },
  RETAIL: { label: 'Retail', description: 'Retail accounts', isEnforced: false },
};

/** What saving `draft` over `saved` would change, for the save bar: "Adds Brokers and 2 staff accounts". */
export const describeChanges = (saved: ExchangeMaintenanceAllowlist, draft: ExchangeMaintenanceAllowlist): string => {
  const addedSegments = draft.segments.filter((segment) => !saved.segments.includes(segment)).map((segment) => SEGMENT_COPY[segment].label);
  const removedSegments = saved.segments.filter((segment) => !draft.segments.includes(segment)).map((segment) => SEGMENT_COPY[segment].label);
  const addedIds = draft.account_holder_ids.filter((id) => !saved.account_holder_ids.includes(id)).length;
  const removedIds = saved.account_holder_ids.filter((id) => !draft.account_holder_ids.includes(id)).length;
  const join = (parts: string[]) => parts.join(', ').replace(/, ([^,]*)$/, ' and $1');
  const added = [...addedSegments, ...(addedIds ? [plural(addedIds, 'staff account')] : [])];
  const removed = [...removedSegments, ...(removedIds ? [plural(removedIds, 'staff account')] : [])];
  const sentences = [...(added.length ? [`Adds ${join(added)}`] : []), ...(removed.length ? [`Removes ${join(removed)}`] : [])];
  return sentences.join('. ');
};
